import { Button, Tabs } from "@mantine/core";
import { useEffect, useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { type ApiResponse } from "../../../api";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import type { WizardFormModeProps } from "../../../types/wizard.types";
import {
  type Person,
  type Site,
  type SiteCategoriesSummaryResponse
} from "../../../utils/backend-types";
import { collectHighlightedTabs } from "../../../utils/collectHighlightedTabs";
import { applyHighlightingToObjectFormFieldData } from "../../../utils/react-hook-form/applyHighlightingToObjectFormFieldData";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { PERSON_WIZARD_TABS } from "../PersonWizard.constants";
import { DeliveryAndInvoicesFields } from "./DeliveryAndInvoicesFields/DeliveryAndInvoicesFields";
import { GeneralFields } from "./GeneralFields/GeneralFields";
import { usePersonFormVisibility } from "./hooks/usePersonFormVisibility";
import { PERSON_FORM_FIELD_DATA } from "./PersonForm.constants";
import { RegulatoryDutiesFields } from "./RegulatoryDutiesFields/RegulatoryDutiesFields";
import { TaxFormFields } from "./TaxFormFields/TaxFormFields";

type PersonFormProps = {
  variantId: number;
  showFooter?: boolean;
  showStaffView?: boolean;
  siteCategories?: SiteCategoriesSummaryResponse;
  persons: Array<Person>;
  sites: Array<Site>;
  onCancel: () => void;
  onChangeNumErrors?: (numErrors: number) => void;
  onChangeNumHighlightedFields?: (
    numHighlightedFields: number,
    highlightedTabs: Array<string>
  ) => void;
  onSubmitCreate: (data: Person) => Promise<ApiResponse<Person>>;
  onSubmitUpdate: (data: Person) => Promise<ApiResponse<Person>>;
} & WizardFormModeProps<Person>;

function PersonForm({
  variantId,
  showFooter = true,
  showStaffView,
  siteCategories,
  persons,
  sites,
  onCancel,
  onChangeNumErrors,
  onChangeNumHighlightedFields,
  onSubmitCreate,
  onSubmitUpdate,
  ...modeProps
}: PersonFormProps) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    setValue,
    watch
  } = useForm<Person>({
    defaultValues:
      modeProps.mode === "edit" ? modeProps.wizardObject : undefined
  });
  const { formFieldData, numHighlightedFields } =
    applyHighlightingToObjectFormFieldData(
      PERSON_FORM_FIELD_DATA,
      watch,
      modeProps.mode === "edit" ? modeProps : undefined
    );
  const { t } = useTranslation();
  const { showErrorToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const visibility = usePersonFormVisibility({
    modeProps,
    sites,
    siteCategories,
    watch,
    setValue
  });

  useEffect(() => {
    onChangeNumErrors?.(Object.keys(errors).length);
  }, [errors, onChangeNumErrors]);

  const highlightedTabs = collectHighlightedTabs(formFieldData);
  const highlightedTabsKey = highlightedTabs.join(",");

  useEffect(() => {
    onChangeNumHighlightedFields?.(numHighlightedFields, highlightedTabs);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- highlightedTabsKey is a stable string representation of highlightedTabs
  }, [numHighlightedFields, highlightedTabsKey, onChangeNumHighlightedFields]);

  const onValidSubmit: SubmitHandler<Person> = async (data) => {
    setIsSubmitting(true);

    try {
      let response: ApiResponse<Person>;

      if (modeProps.mode === "create") {
        response = await onSubmitCreate(data);
      } else {
        response = await onSubmitUpdate(data);
      }

      return response;
    } catch (error) {
      setIsSubmitting(false);
      setErrorsFromResponseData<Person>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  };

  const submitCallback = handleSubmit(onValidSubmit);

  function handleClickCancel() {
    reset();
    onCancel();
  }

  return (
    <form className="PersonForm" onSubmit={submitCallback}>
      <Tabs.Panel value={PERSON_WIZARD_TABS[0].value}>
        <GeneralFields
          control={control}
          fieldsThatCouldBeMissing={
            modeProps.mode === "edit"
              ? modeProps.fieldsThatCouldBeMissing
              : undefined
          }
          formFieldData={formFieldData}
          visibility={visibility.generalFieldsVisibility}
        />
      </Tabs.Panel>

      {visibility.showRegulatoryDutiesTab && (
        <Tabs.Panel value={PERSON_WIZARD_TABS[1].value}>
          <RegulatoryDutiesFields
            control={control}
            formFieldData={formFieldData}
            isManufacturingCompany={visibility.isManufacturingCompany}
            showEigenerzeugungPlusFields={
              visibility.showEigenerzeugungPlusFields
            }
            showSmallMediumEnterpriseField={
              visibility.showSmallMediumEnterpriseField
            }
          />
        </Tabs.Panel>
      )}

      <Tabs.Panel value={PERSON_WIZARD_TABS[2].value}>
        <TaxFormFields
          control={control}
          errors={errors}
          formFieldData={formFieldData}
          personId={modeProps.mode === "edit" ? modeProps.wizardObject.id : 0}
          persons={persons}
          taxFormsSubmissionInOwnName={visibility.taxFormsSubmissionInOwnName}
        />
      </Tabs.Panel>

      <Tabs.Panel value={PERSON_WIZARD_TABS[3].value}>
        <DeliveryAndInvoicesFields
          control={control}
          formFieldData={formFieldData}
          invoiceSuppliedAddressIsGeneralAddress={
            visibility.invoiceSuppliedAddressIsGeneralAddress
          }
          invoiceSupplierIsGbr={visibility.invoiceSupplierIsGbr}
          isParagraph6EegActive={visibility.isParagraph6EegActive}
          isSolarContractor={visibility.isSolarContractor}
          personId={modeProps.mode === "edit" ? modeProps.wizardObject.id : 0}
          projectHasSitesWithInvoicing={visibility.projectHasSitesWithInvoicing}
          siteCategories={siteCategories}
        />
      </Tabs.Panel>

      {showFooter && (
        <ModalFooter>
          <Button
            disabled={isSubmitting}
            variant="default"
            onClick={handleClickCancel}
          >
            Abbrechen
          </Button>
          <Button
            color="secondary"
            loading={isSubmitting}
            onClick={submitCallback}
          >
            {modeProps.mode === "edit" ? "Speichern" : "Erstellen"}
          </Button>
        </ModalFooter>
      )}
    </form>
  );
}

export { PersonForm, type PersonFormProps };
